import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const ApprovalManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(false);

  // Approval Requests State
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvalRecords, setApprovalRecords] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [actionComments, setActionComments] = useState('');

  useEffect(() => {
    loadApprovalData();
  }, []);

  const loadApprovalData = async () => {
    setLoading(true);
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

      // Load pending requests (super admin only)
      const pendingRes = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/approval/requests`, {
        method: 'GET',
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (pendingRes.ok) {
        const pending = await pendingRes.json();
        setPendingRequests(pending);
      }

      // Load approval records
      const recordsRes = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/approval/records`, {
        method: 'GET',
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (recordsRes.ok) {
        const records = await recordsRes.json();
        setApprovalRecords(records);
      }

      // Load my requests
      const myRequestsRes = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/approval/my-requests`, {
        method: 'GET',
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (myRequestsRes.ok) {
        const myReqs = await myRequestsRes.json();
        setMyRequests(myReqs);
      }

    } catch (error) {
      console.error('Error loading approval data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRequest = async (requestId, action, comments = '') => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/approval/requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({
          action: action,
          comments: comments
        })
      });

      if (response.ok) {
        alert(`Request ${action}d successfully!`);
        setShowActionModal(false);
        setActionComments('');
        setSelectedRequest(null);
        loadApprovalData(); // Refresh data
      } else {
        const error = await response.json();
        alert(`Failed to ${action} request: ${error.error}`);
      }
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      alert(`Failed to ${action} request`);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this approval request?')) {
      return;
    }

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/approval/requests/${requestId}`, {
        method: 'DELETE',
        headers: {
          'admin-email': adminUser.email
        }
      });

      if (response.ok) {
        alert('Request cancelled successfully!');
        loadApprovalData(); // Refresh data
      } else {
        alert('Failed to cancel request');
      }
    } catch (error) {
      console.error('Error cancelling request:', error);
      alert('Failed to cancel request');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'executed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return '➕';
      case 'delete': return 'Delete';
      default: return 'Note';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading approval management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Approval Management</h1>
            <p className="text-gray-600 mt-2">Manage approval requests and workflow</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b">
            <nav className="flex flex-wrap">
              {[
                { id: 'pending', label: 'Pending Requests', icon: '?', count: pendingRequests.length },
                { id: 'records', label: 'Approval Records', icon: 'Applications', count: approvalRecords.length },
                { id: 'my-requests', label: 'My Requests', icon: 'Profile', count: myRequests.length }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-medium flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tab.id === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Pending Requests Tab */}
            {activeTab === 'pending' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-800">Pending Approval Requests</h2>
                </div>

                {pendingRequests.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">Loading</div>
                    <p className="text-gray-600">No pending approval requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingRequests.map((request) => (
                      <div key={request.id} className="bg-white border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="text-lg">{getActionIcon(request.action)}</span>
                              <h3 className="font-bold text-lg text-gray-800 capitalize">
                                {request.action} {request.resourceType}
                              </h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                {request.status}
                              </span>
                            </div>

                            <p className="text-gray-600 mb-2">{request.reason}</p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-500">
                              <div>
                                <span className="font-medium">Requested by:</span><br />
                                {request.requesterName} ({request.requesterRole})
                              </div>
                              <div>
                                <span className="font-medium">Resource:</span><br />
                                {request.resourceName}
                              </div>
                              <div>
                                <span className="font-medium">Requested:</span><br />
                                {formatDate(request.requestedAt)}
                              </div>
                            </div>
                          </div>

                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => {
                                setSelectedRequest(request);
                                setShowDetailsModal(true);
                              }}
                              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            >
                              View Details
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRequest(request);
                                setActionType('approve');
                                setShowActionModal(true);
                              }}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRequest(request);
                                setActionType('reject');
                                setShowActionModal(true);
                              }}
                              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Approval Records Tab */}
            {activeTab === 'records' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-800">Approval Records</h2>
                </div>

                {approvalRecords.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">Applications</div>
                    <p className="text-gray-600">No approval records found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Action</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Resource</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Requester</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Reviewed By</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {approvalRecords.map((record) => (
                          <tr key={record.id} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center space-x-2">
                                <span>{getActionIcon(record.action)}</span>
                                <span className="capitalize">{record.action} {record.resourceType}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-800">
                              {record.resourceName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {record.requesterName}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(record.status)}`}>
                                {record.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {record.reviewerName || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {formatDate(record.reviewedAt)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={() => {
                                  setSelectedRequest(record);
                                  setShowDetailsModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* My Requests Tab */}
            {activeTab === 'my-requests' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-800">My Approval Requests</h2>
                </div>

                {myRequests.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">Note</div>
                    <p className="text-gray-600">You haven't submitted any approval requests yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Action</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Resource</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Requested</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myRequests.map((request) => (
                          <tr key={request.id} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center space-x-2">
                                <span>{getActionIcon(request.action)}</span>
                                <span className="capitalize">{request.action} {request.resourceType}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-800">
                              {request.resourceName}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(request.status)}`}>
                                {request.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {formatDate(request.requestedAt)}
                            </td>
                            <td className="px-4 py-3 text-sm space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setShowDetailsModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Details
                              </button>
                              {request.status === 'pending' && (
                                <button
                                  onClick={() => handleCancelRequest(request.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Cancel
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Request Details</h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedRequest(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Request Information</h3>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Action:</span> {selectedRequest.action} {selectedRequest.resourceType}</div>
                      <div><span className="font-medium">Resource:</span> {selectedRequest.resourceName}</div>
                      <div><span className="font-medium">Status:</span>
                        <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedRequest.status)}`}>
                          {selectedRequest.status}
                        </span>
                      </div>
                      <div><span className="font-medium">Requested:</span> {formatDate(selectedRequest.requestedAt)}</div>
                      {selectedRequest.reviewedAt && (
                        <div><span className="font-medium">Reviewed:</span> {formatDate(selectedRequest.reviewedAt)}</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Requester Information</h3>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Name:</span> {selectedRequest.requesterName}</div>
                      <div><span className="font-medium">Role:</span> {selectedRequest.requesterRole}</div>
                      {selectedRequest.reviewerName && (
                        <div><span className="font-medium">Reviewed by:</span> {selectedRequest.reviewerName}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Reason</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedRequest.reason}</p>
                </div>

                {/* Review Comments */}
                {selectedRequest.reviewComments && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Review Comments</h3>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedRequest.reviewComments}</p>
                  </div>
                )}

                {/* Additional Data */}
                {selectedRequest.studentData && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Student Data</h3>
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div><span className="font-medium">Name:</span> {selectedRequest.studentData.firstName} {selectedRequest.studentData.lastName}</div>
                        <div><span className="font-medium">Class:</span> {selectedRequest.studentData.className}</div>
                        <div><span className="font-medium">Age:</span> {selectedRequest.studentData.age}</div>
                        <div><span className="font-medium">Username:</span> {selectedRequest.studentData.username}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedRequest(null);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal (Approve/Reject) */}
      {showActionModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 capitalize">
                  {actionType} Request
                </h2>
                <button
                  onClick={() => {
                    setShowActionModal(false);
                    setSelectedRequest(null);
                    setActionComments('');
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to <span className="font-medium">{actionType}</span> this approval requestLoading
                  </p>
                  <div className="bg-gray-50 p-3 rounded text-sm">
                    <div className="font-medium">{selectedRequest.resourceName}</div>
                    <div className="text-gray-600">{selectedRequest.reason}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comments (Optional)
                  </label>
                  <textarea
                    value={actionComments}
                    onChange={(e) => setActionComments(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Add any comments..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowActionModal(false);
                    setSelectedRequest(null);
                    setActionComments('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleProcessRequest(selectedRequest.id, actionType, actionComments)}
                  className={`px-4 py-2 text-white rounded-md ${
                    actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {actionType === 'approve' ? 'Approve' : 'Reject'} Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalManagement;
